import { useState, useEffect, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';

const useDebounce = <T>(initialValue?: any, delay = 300) => {
  const [value, setValue] = useState<T>(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => setValue(initialValue), delay);
    return () => clearTimeout(handler);
  }, [initialValue, delay]);

  return [value, setValue] as const;
};

const useDebounceState = <T>(initialValue?: any, delay = 300) => {
  const [value, setValue] = useState<T>(initialValue);
  const [debounceValue, setNow] = useDebounce<T>(value, delay);

  return [debounceValue, setValue, setNow] as const;
};

export { useDebounce, useDebounceState };

// https://www.developerway.com/posts/debouncing-in-react
export const useDebounceCallback = (callback: (...args: any) => void, wait = 1000) => {
  const ref = useRef<() => void>();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const func = (...args: any) => {
      ref.current?.apply(null, args);
    };

    return debounce(func, wait);
  }, [wait]);

  return debouncedCallback;
};
